// Frontend module registry — the single source of truth for RBAC modules.
// Keep in sync with backend/modules/registry.py (scaffold_module.py updates both).
export const MODULES = [
  { key: "seo", label: "On-Page SEO Optimiser", route: "/seo", icon: "Search", status: "live", desc: "Claude-scored page audits, meta tags & fixes." },
  { key: "faq", label: "FAQs Creator", route: "/faq", icon: "HelpCircle", status: "live", desc: "Generate SEO FAQs with JSON-LD schema." },
  { key: "theme_design", label: "Theme Design", route: "/theme-design", icon: "Palette", status: "live", desc: "Govern colour, type, spacing & radius tokens." },
  { key: "assetlibrary", label: "Asset Library", route: "/asset-library", icon: "Library", status: "live", desc: "Unified DAM — artwork design files with search, tags & files." },
  { key: "products", label: "Marketing Products", route: "/products", icon: "Package", status: "live", desc: "Catalog of orderable products linked to their artwork." },
  { key: "suppliersquotes", label: "Suppliers & Quotes", route: "/suppliers-quotes", icon: "Handshake", status: "live", desc: "Supplier directory + RFQ workflow: quote requests, quotes and a chosen winner." },
  { key: "requests", label: "Marketing Requests", route: "/requests", icon: "ClipboardList", status: "live", desc: "Staff request portal — raise requests, track them, link quotes and generate a PO." },
  { key: "sponsorship", label: "Sponsorship Requests", route: "/sponsorship", icon: "Gift", status: "live", desc: "Review, approve and track sponsorship applications from the Dowdens Group website." },
  { key: "payments", label: "Payments Register", route: "/payments", icon: "Wallet", status: "live", desc: "Track supplier payments (invoices & receipts) through Draft → Submitted to Accounts → Processed, with attachments, branch cost-splitting, totals, CSV export and workflow emails." },
  { key: "orchestrator", label: "Orchestrator", route: "/orchestrator", icon: "Workflow", status: "live", desc: "Plan new modules through Draft → Ready → Planned → Building → Live." },
  { key: "campaign_signals", label: "Marketing Campaign Signals", route: "/campaign-signals", icon: "TrendingUp", status: "live", desc: "Demand trends by brand, service and location from Group Leads, with nightly campaign signals. Managers and Admins only." },
  { key: "group_leads", label: "Group Leads", route: "/group-leads", icon: "Inbox", status: "live", desc: "Unified customer-enquiry inbox, auto-screened for spam and routed to the right manager." },
  { key: "website_manager", label: "Website Manager", route: "/website-manager", icon: "Globe2", status: "live", desc: "Registry of every page on the group's websites, synced nightly from WordPress, with SEO gaps and a Google Drive folder per page." },
  // SCAFFOLD_MODULE_ENTRIES
];

export const MODULE_META = Object.fromEntries(MODULES.map((m) => [m.key, m]));
export const MODULE_ORDER = MODULES.map((m) => m.key);
export const ROUTE_MODULE = Object.fromEntries(MODULES.map((m) => [m.route, m.key]));

// ------------------------------------------------------------ 4-tier RBAC
export const ROLES = ["developer", "admin", "manager", "purchasing", "member"];
export const ROLE_LEVEL = { member: 1, purchasing: 1, manager: 2, admin: 3, developer: 4 };
export const ROLE_LABELS = { developer: "Developer", admin: "Admin", manager: "Manager", purchasing: "Purchasing", member: "Member" };
export const ROLE_DESC = {
  developer: "Full access, including build tools & the Orchestrator.",
  admin: "Manage users, access, modules & view the audit log.",
  manager: "View module results & dashboards, assign members.",
  purchasing: "Handle purchasing: view RFQs, record PO numbers & manage requests.",
  member: "Run the modules they've been granted.",
};

export function roleLevel(user) {
  return ROLE_LEVEL[user?.role] || 0;
}

// Developer & Admin — full module/site access and admin surfaces.
export function isPrivileged(user) {
  return roleLevel(user) >= ROLE_LEVEL.admin;
}

export function hasRole(user, minRole) {
  return roleLevel(user) >= (ROLE_LEVEL[minRole] || 0);
}

export const isDeveloper = (user) => user?.role === "developer";

export function canAccessModule(user, moduleKey) {
  if (!user) return false;
  if (isPrivileged(user) || user.modules === "all") return true;
  return Array.isArray(user.modules) && user.modules.includes(moduleKey);
}
